import { useState, useRef } from 'react'
import { useToast } from '../../hooks/useToast'
import { supabase } from '../../lib/supabase'
import { Camera, Upload, X, Loader2 } from 'lucide-react'

/**
 * Profile Tab - User Profile Customization
 * Features: Avatar, Banner, Display Name, Bio, Custom Status, Pronouns
 */
export default function ProfileTab({ profile, onUpdateProfile }) {
  const toast = useToast()
  const avatarInputRef = useRef(null)
  const bannerInputRef = useRef(null)

  // Form state
  const [formData, setFormData] = useState({
    display_name: profile?.display_name || profile?.username || '',
    bio: profile?.bio || '',
    custom_status: profile?.custom_status || '',
    pronouns: profile?.pronouns || ''
  })

  // Upload states
  const [avatarPreview, setAvatarPreview] = useState(profile?.avatar_url || null)
  const [bannerPreview, setBannerPreview] = useState(profile?.banner_url || null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)

  // Character limits
  const BIO_MAX_LENGTH = 500

  // Handle avatar file selection
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, GIF)')
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Avatar must be under 5MB')
      return
    }

    try {
      setUploadingAvatar(true)

      // Create preview
      const previewUrl = URL.createObjectURL(file)
      setAvatarPreview(previewUrl)

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`

      const { data, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        // Check if bucket exists, if not provide helpful error
        if (uploadError.message.includes('Bucket not found')) {
          toast.error('Avatar storage not configured. Please contact administrator.')
          console.error('Avatars bucket does not exist. Create it in Supabase Dashboard.')
        } else {
          throw uploadError
        }
        setAvatarPreview(profile?.avatar_url || null)
        return
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      // Delete old avatar if exists
      if (profile.avatar_url && profile.avatar_url.includes('supabase')) {
        const oldFileName = profile.avatar_url.split('/').pop()
        await supabase.storage.from('avatars').remove([oldFileName])
      }

      // Update profile in database
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id)

      if (updateError) throw updateError

      // Update local state
      onUpdateProfile({ ...profile, avatar_url: publicUrl })
      toast.success('Avatar updated successfully!')

    } catch (err) {
      console.error('Error uploading avatar:', err)
      toast.error('Failed to upload avatar')
      setAvatarPreview(profile?.avatar_url || null)
    } finally {
      setUploadingAvatar(false)
    }
  }

  // Handle banner file selection
  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, GIF)')
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Banner must be under 10MB')
      return
    }

    try {
      setUploadingBanner(true)

      // Create preview
      const previewUrl = URL.createObjectURL(file)
      setBannerPreview(previewUrl)

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`

      const { data, error: uploadError } = await supabase.storage
        .from('banners')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        // Check if bucket exists, if not provide helpful error
        if (uploadError.message.includes('Bucket not found')) {
          toast.error('Banner storage not configured. Please contact administrator.')
          console.error('Banners bucket does not exist. Create it in Supabase Dashboard.')
        } else {
          throw uploadError
        }
        setBannerPreview(profile?.banner_url || null)
        return
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('banners')
        .getPublicUrl(fileName)

      // Delete old banner if exists
      if (profile.banner_url && profile.banner_url.includes('supabase')) {
        const oldFileName = profile.banner_url.split('/').pop()
        await supabase.storage.from('banners').remove([oldFileName])
      }

      // Update profile in database
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ banner_url: publicUrl })
        .eq('id', profile.id)

      if (updateError) throw updateError

      // Update local state
      onUpdateProfile({ ...profile, banner_url: publicUrl })
      toast.success('Banner updated successfully!')

    } catch (err) {
      console.error('Error uploading banner:', err)
      toast.error('Failed to upload banner')
      setBannerPreview(profile?.banner_url || null)
    } finally {
      setUploadingBanner(false)
    }
  }

  // Remove avatar
  const handleRemoveAvatar = async () => {
    try {
      setUploadingAvatar(true)

      // Delete from storage if exists
      if (profile.avatar_url && profile.avatar_url.includes('supabase')) {
        const fileName = profile.avatar_url.split('/').pop()
        await supabase.storage.from('avatars').remove([fileName])
      }

      // Update database
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', profile.id)

      if (error) throw error

      setAvatarPreview(null)
      onUpdateProfile({ ...profile, avatar_url: null })
      toast.success('Avatar removed')

    } catch (err) {
      console.error('Error removing avatar:', err)
      toast.error('Failed to remove avatar')
    } finally {
      setUploadingAvatar(false)
    }
  }

  // Remove banner
  const handleRemoveBanner = async () => {
    try {
      setUploadingBanner(true)

      // Delete from storage if exists
      if (profile.banner_url && profile.banner_url.includes('supabase')) {
        const fileName = profile.banner_url.split('/').pop()
        await supabase.storage.from('banners').remove([fileName])
      }

      // Update database
      const { error } = await supabase
        .from('profiles')
        .update({ banner_url: null })
        .eq('id', profile.id)

      if (error) throw error

      setBannerPreview(null)
      onUpdateProfile({ ...profile, banner_url: null })
      toast.success('Banner removed')

    } catch (err) {
      console.error('Error removing banner:', err)
      toast.error('Failed to remove banner')
    } finally {
      setUploadingBanner(false)
    }
  }

  // Save profile text fields
  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true)

      // Validate bio length
      if (formData.bio.length > BIO_MAX_LENGTH) {
        toast.error(`Bio must be ${BIO_MAX_LENGTH} characters or less`)
        return
      }

      // Update profile in database
      const updates = {
        display_name: formData.display_name.trim() || profile.username,
        bio: formData.bio.trim(),
        custom_status: formData.custom_status.trim(),
        pronouns: formData.pronouns.trim()
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id)

      if (error) throw error

      // Update local state
      onUpdateProfile({ ...profile, ...updates })
      toast.success('Profile updated successfully!')

    } catch (err) {
      console.error('Error saving profile:', err)
      toast.error('Failed to save profile')
    } finally {
      setSavingProfile(false)
    }
  }

  // Get default avatar (generated)
  const getDefaultAvatar = () => {
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.id || 'default'}`
  }

  return (
    <div className="p-6 max-w-4xl">
      <h3 className="text-xl font-semibold text-white mb-6">Profile Customization</h3>

      {/* Profile Preview Card */}
      <div className="glass-strong rounded-gjyl overflow-hidden mb-6">
        {/* Banner Section */}
        <div className="relative h-32 bg-gradient-to-br from-gjyl-indigo-600 to-gjyl-purple-600 group">
          {bannerPreview && (
            <img
              src={bannerPreview}
              alt="Profile banner"
              className="w-full h-full object-cover"
            />
          )}

          {/* Banner Upload Overlay */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              onClick={() => bannerInputRef.current?.click()}
              disabled={uploadingBanner}
              className="px-3 py-1.5 bg-gjyl-slate-800 hover:bg-gjyl-slate-700 text-white rounded text-sm font-medium transition-colors flex items-center gap-2"
            >
              {uploadingBanner ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Change Banner
                </>
              )}
            </button>
            {bannerPreview && (
              <button
                onClick={handleRemoveBanner}
                disabled={uploadingBanner}
                className="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded text-sm font-medium transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Remove
              </button>
            )}
          </div>

          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleBannerChange}
          />
        </div>

        {/* Avatar Section */}
        <div className="px-6 pb-6">
          <div className="relative -mt-16 mb-4">
            <div className="relative inline-block group">
              {/* Avatar Circle */}
              <div className="w-24 h-24 rounded-gjyl ring-4 ring-gjyl-slate-900 overflow-hidden bg-gjyl-slate-800">
                <img
                  src={avatarPreview || getDefaultAvatar()}
                  alt={profile?.username || 'User'}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Avatar Upload Overlay */}
              <div className="absolute inset-0 bg-black/50 rounded-gjyl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="text-white"
                  title="Change avatar"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6" />
                  )}
                </button>
              </div>

              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>

            {avatarPreview && profile?.avatar_url && (
              <button
                onClick={handleRemoveAvatar}
                disabled={uploadingAvatar}
                className="ml-3 px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-xs font-medium transition-colors"
              >
                Remove Avatar
              </button>
            )}
          </div>

          {/* Profile Form */}
          <div className="space-y-4">
            {/* Username (Read-only) */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                Username (Read-only)
              </label>
              <div className="px-3 py-2 bg-gjyl-slate-800/50 text-slate-500 rounded border border-gjyl-slate-700/50 text-sm">
                @{profile?.username || 'username'}
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder={profile?.username || 'Enter display name'}
                className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm"
              />
            </div>

            {/* Pronouns */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                Pronouns (Optional)
              </label>
              <input
                type="text"
                value={formData.pronouns}
                onChange={(e) => setFormData({ ...formData, pronouns: e.target.value })}
                placeholder="e.g., they/them, she/her, he/him"
                className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm"
              />
            </div>

            {/* Custom Status */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                Custom Status (Optional)
              </label>
              <input
                type="text"
                value={formData.custom_status}
                onChange={(e) => setFormData({ ...formData, custom_status: e.target.value })}
                placeholder="What's on your mind?"
                maxLength={100}
                className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                About Me
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                rows={4}
                maxLength={BIO_MAX_LENGTH}
                className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm resize-none"
              />
              <div className="mt-1 text-xs text-slate-500 text-right">
                {formData.bio.length} / {BIO_MAX_LENGTH}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="px-4 py-2 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-600 text-white rounded font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="glass rounded-gjyl p-4">
        <h4 className="text-sm font-semibold text-white mb-2">Profile Tips</h4>
        <ul className="text-xs text-slate-400 space-y-1">
          <li>• Avatar images support PNG, JPG, and animated GIFs (max 5MB)</li>
          <li>• Banner images recommended size: 1200x400px (max 10MB)</li>
          <li>• Hover over your avatar or banner to upload or change them</li>
          <li>• Your username cannot be changed, but you can set a display name</li>
          <li>• All changes are saved immediately to your profile</li>
        </ul>
      </div>
    </div>
  )
}
